import OlAttribution from 'ol/control/Attribution';
import { useEffect, useMemo, useRef } from 'react';
var infoIcon = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2LjAyNyIgaGVpZ2h0PSIxMy42NDEiPjxnIGlkPSJpbmZvSWNvbi1Hcm91cF80MDAyIiBkYXRhLW5hbWU9Ikdyb3VwIDQwMDIiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC0yOTEgLTcyOCkiPjxnIGlkPSJpbmZvSWNvbi1fMTUxNzc2IiBkYXRhLW5hbWU9IjE1MTc3NiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMjU5Ljg2MSA3MjgpIj48cGF0aCBpZD0iaW5mb0ljb24tUGF0aF85Njk0IiBkYXRhLW5hbWU9IlBhdGggOTY5NCIgZD0iTTM2Ljk4NiAxMi4xNjlsLS4xOTEuNzc5cS0uODU4LjMzOS0xLjM2OS41MTZhMy42MSAzLjYxIDAgMDEtMS4xODcuMTc3IDIuMzY5IDIuMzY5IDAgMDEtMS42MTUtLjUwNiAxLjYzOSAxLjYzOSAwIDAxLS41NzYtMS4yODYgNC41NTEgNC41NTEgMCAwMS4wNDMtLjYxOXEuMDQ0LS4zMTcuMTM5LS43MTVsLjcxNS0yLjUyOXEuMS0uMzYzLjE2MS0uNjg5YTIuOTkzIDIuOTkzIDAgMDAuMDY1LS41OTMuOTE3LjkxNyAwIDAwLS4yLS42NzYgMS4xMyAxLjEzIDAgMDAtLjc2NC0uMTkxIDIgMiAwIDAwLS41NjguMDg3IDcuMzU5IDcuMzU5IDAgMDAtLjUuMTY1bC4xOTEtLjc4cS43LS4yODYgMS4zNDQtLjQ4OWE0LjAxOCA0LjAxOCAwIDAxMS4yMTUtLjIgMi4zMTEgMi4zMTEgMCAwMTEuNTkuNSAxLjY1NCAxLjY1NCAwIDAxLjU1OSAxLjI5NWMwIC4xMS0uMDEyLjMtLjAzOS41OGEzLjg4MiAzLjg4MiAwIDAxLS4xNDMuNzYzbC0uNzEyIDIuNTE5YTYuNjkyIDYuNjkyIDAgMDAtLjE1Ni42OTQgMy41NzIgMy41NzIgMCAwMC0uMDcuNTg5Ljg0OC44NDggMCAwMC4yMjQuNjg0IDEuMjY1IDEuMjY1IDAgMDAuNzc5LjE4MiAyLjI0OCAyLjI0OCAwIDAwLjU4OS0uMDkxIDMuMjkxIDMuMjkxIDAgMDAuNDc2LS4xNjZ6bS4xOC0xMC41NzZhMS40NzQgMS40NzQgMCAwMS0uNSAxLjEyMiAxLjcgMS43IDAgMDEtMS4yLjQ2NCAxLjcxMyAxLjcxMyAwIDAxLTEuMi0uNDY0IDEuNDcxIDEuNDcxIDAgMDEtLjUtMS4xMjIgMS40ODUgMS40ODUgMCAwMS41LTEuMTI2IDEuNzc4IDEuNzc4IDAgMDEyLjQgMCAxLjQ4OCAxLjQ4OCAwIDAxLjUgMS4xMjZ6IiBmaWxsPSIjMGIxZjNjIi8+PC9nPjwvZz48L3N2Zz4=";
import usePrevious from '../hooks/usePrevious';
import { useOpenLayers } from '../openlayers-context';
import { getOptions } from '../utils';

var getAttributionOptionsDefaults = (options, mapWidth) => {
  var attributionButtonLabel = document.createElement('img');
  attributionButtonLabel.className = 'ol-attribution-label';
  attributionButtonLabel.src = infoIcon;
  attributionButtonLabel.alt = 'Toggle attributions';
  var shouldBeCollapsed = mapWidth && mapWidth < 768 || false;
  return {
    collapsible: options.collapsible || true,
    collapsed: options.collapsed || shouldBeCollapsed,
    label: options.label || attributionButtonLabel,
    collapseLabel: options.label || attributionButtonLabel,
    target: 'ol-controls-attribution-target',
    className: 'ol-attribution-override'
  };
}; // OL Options


var allAttributionOptions = {
  className: undefined,
  target: undefined,
  collapsible: undefined,
  collapsed: undefined,
  tipLabel: undefined,
  label: undefined,
  collapseLabel: undefined,
  render: undefined
};

var Attribution = props => {
  var {
    map
  } = useOpenLayers();
  var prevCollapsed = usePrevious(props.collapsed);
  var control = useRef();
  var memoizedOptions = useMemo(() => getOptions(Object.assign(allAttributionOptions, props)), [props]);
  useEffect(() => {
    var _map$getSize;

    if (!map) return;
    var currentControl = control.current;
    var hasInit = false;
    var optionsWithDefaults = getAttributionOptionsDefaults(memoizedOptions, ((_map$getSize = map.getSize()) === null || _map$getSize === void 0 ? void 0 : _map$getSize[0]) || null);
    currentControl = new OlAttribution(optionsWithDefaults);

    var onMoveStart = () => {
      if (currentControl && hasInit && !currentControl.getCollapsed()) {
        currentControl.setCollapsed(true);
      } else hasInit = true;
    };

    map.on('movestart', onMoveStart);
    map.addControl(currentControl);
    return () => {
      if (currentControl) {
        map.removeControl(currentControl);
      }

      map.un('movestart', onMoveStart);
    };
  }, [map, memoizedOptions]);
  useEffect(() => {
    if (props.collapsed !== prevCollapsed) {
      var _control$current;

      (_control$current = control.current) === null || _control$current === void 0 ? void 0 : _control$current.setCollapsed(!!props.collapsed);
    }
  }, [prevCollapsed, props.collapsed]);
  return null;
};

export default Attribution;